import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';
import type { InvoiceStatus } from './useInvoices';

export interface PortalInvoiceItem {
  id: string;
  clinic_id: string;
  clinic_name: string;
  invoice_number: string | null;
  status: InvoiceStatus;
  currency: string;
  subtotal_cents: number;
  discount_cents: number;
  vat_cents: number;
  total_cents: number;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  created_at: string;
  pay_token: string | null;
  pay_url: string | null;
}

export const portalInvoicesQueryKey = (patientId: string | null) =>
  ['portal', 'invoices', patientId] as const;

export function usePortalInvoices() {
  const { activePatientId, isLoading: clinicsLoading } = usePortalActiveClinic();

  const query = useQuery({
    queryKey: portalInvoicesQueryKey(activePatientId),
    enabled: Boolean(activePatientId),
    queryFn: async (): Promise<PortalInvoiceItem[]> => {
      if (!activePatientId) return [];

      const { data, error } = await supabase
        .from('invoices')
        .select(
          `id, clinic_id, invoice_number, status, currency,
           subtotal_cents, discount_cents, vat_cents, total_cents,
           issued_at, due_at, paid_at, created_at,
           pay_token,
           clinic:clinics!invoices_clinic_id_fkey(name)`
        )
        .eq('patient_id', activePatientId)
        .neq('status', 'void')
        .order('created_at', { ascending: false });

      if (error) throw error;

      type Joined = {
        id: string;
        clinic_id: string;
        invoice_number: string | null;
        status: InvoiceStatus;
        currency: string;
        subtotal_cents: number;
        discount_cents: number;
        vat_cents: number;
        total_cents: number;
        issued_at: string | null;
        due_at: string | null;
        paid_at: string | null;
        created_at: string;
        pay_token: string | null;
        clinic: { name: string } | null;
      };

      const origin = typeof window !== 'undefined' ? window.location.origin : '';

      return ((data ?? []) as unknown as Joined[]).map(
        (r): PortalInvoiceItem => ({
          id: r.id,
          clinic_id: r.clinic_id,
          clinic_name: r.clinic?.name ?? '—',
          invoice_number: r.invoice_number,
          status: r.status,
          currency: r.currency,
          subtotal_cents: r.subtotal_cents,
          discount_cents: r.discount_cents,
          vat_cents: r.vat_cents,
          total_cents: r.total_cents,
          issued_at: r.issued_at,
          due_at: r.due_at,
          paid_at: r.paid_at,
          created_at: r.created_at,
          pay_token: r.pay_token,
          pay_url: r.pay_token ? `${origin}/pay-invoice/${r.pay_token}` : null,
        })
      );
    },
    staleTime: 15_000,
  });

  return {
    ...query,
    isLoading: clinicsLoading || query.isLoading,
  };
}

export function usePortalDownloadInvoice() {
  return useMutation({
    mutationFn: async (invoiceId: string): Promise<string> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data: { storage_path: string; signed_url: string };
      }>('render-invoice-pdf', { body: { invoice_id: invoiceId } });

      if (error) throw error;
      if (!data?.ok || !data.data) throw new Error('PDF render failed');

      return data.data.signed_url;
    },
  });
}
